import React, { useState } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Stepper,
  Step,
  StepLabel,
  Divider,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  FormGroup,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import {
  LocalShipping as ShippingIcon,
  Inventory as InventoryIcon,
  Assignment as AssignmentIcon,
  AttachMoney as MoneyIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  Description as DescriptionIcon,
  Download as DownloadIcon,
  Timeline as TimelineIcon,
} from '@mui/icons-material';
import { DashboardLayout } from '../../components/layout/DashboardLayout';

interface OrderItem {
  partNumber: string;
  name: string;
  quantity: number;
  unitPrice: number;
  supplier: string;
}

interface Order {
  id: string;
  orderDate: Date;
  expectedDelivery: Date;
  status: 'pending' | 'processing' | 'shipped' | 'delivered' | 'cancelled';
  trackingNumber: string;
  supplier: string;
  items: OrderItem[];
  totalAmount: number;
  priority: 'standard' | 'express' | 'urgent';
  vehicle: {
    make: string;
    model: string;
    year: number;
  };
  shipping: {
    method: string;
    courier: string;
    address: string;
  };
  documents: {
    type: string;
    url: string;
  }[];
}

interface NewOrderForm {
  partNumber: string;
  partName: string;
  quantity: number;
  supplier: string;
  vehicle: {
    make: string;
    model: string;
    year: number;
  };
  priority: string;
}

interface FilterForm {
  status: string[];
  priority: string[];
  dateRange: {
    start: string;
    end: string;
  };
  supplier: string;
}

export const OrderTracking: React.FC = () => {
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [openNewOrderDialog, setOpenNewOrderDialog] = useState(false);
  const [newOrder, setNewOrder] = useState<NewOrderForm>({
    partNumber: '',
    partName: '',
    quantity: 1,
    supplier: '',
    vehicle: {
      make: '',
      model: '',
      year: new Date().getFullYear(),
    },
    priority: 'standard',
  });
  const [openFilterDialog, setOpenFilterDialog] = useState(false);
  const [filters, setFilters] = useState<FilterForm>({
    status: [],
    priority: [],
    dateRange: {
      start: '',
      end: '',
    },
    supplier: '',
  });

  // Mock order data for luxury vehicles
  const orders: Order[] = [
    {
      id: 'ORD001',
      orderDate: new Date(2024, 2, 15),
      expectedDelivery: new Date(2024, 2, 20),
      status: 'shipped',
      trackingNumber: 'FER-24-78901',
      supplier: 'Ferrari South Africa',
      items: [
        {
          partNumber: 'F488-BP-001',
          name: 'Carbon Ceramic Brake System',
          quantity: 1,
          unitPrice: 212750,
          supplier: 'Ferrari South Africa',
        },
      ],
      totalAmount: 212750,
      priority: 'express',
      vehicle: {
        make: 'Ferrari',
        model: '488 Pista',
        year: 2024,
      },
      shipping: {
        method: 'Air Freight',
        courier: 'DHL Express',
        address: 'Melrose Arch, Johannesburg',
      },
      documents: [
        { type: 'Invoice', url: '/documents/inv-001.pdf' },
        { type: 'Shipping Label', url: '/documents/ship-001.pdf' },
        { type: 'Certificate of Authenticity', url: '/documents/cert-001.pdf' },
      ],
    },
    {
      id: 'ORD002',
      orderDate: new Date(2024, 2, 16),
      expectedDelivery: new Date(2024, 2, 19),
      status: 'processing',
      trackingNumber: 'MCL-24-56789',
      supplier: 'McLaren Johannesburg',
      items: [
        {
          partNumber: 'MCL-765-ECU',
          name: 'Engine Control Unit',
          quantity: 1,
          unitPrice: 245000,
          supplier: 'McLaren Johannesburg',
        },
      ],
      totalAmount: 245000,
      priority: 'urgent',
      vehicle: {
        make: 'McLaren',
        model: '765LT',
        year: 2023,
      },
      shipping: {
        method: 'Priority Air',
        courier: 'FedEx Priority',
        address: 'Sandton, Johannesburg',
      },
      documents: [
        { type: 'Invoice', url: '/documents/inv-002.pdf' },
        { type: 'Technical Specifications', url: '/documents/tech-002.pdf' },
      ],
    },
    {
      id: 'ORD003',
      orderDate: new Date(2024, 2, 17),
      expectedDelivery: new Date(2024, 2, 22),
      status: 'pending',
      trackingNumber: 'POR-24-34567',
      supplier: 'Porsche Centre SA',
      items: [
        {
          partNumber: 'GT3-AW-001',
          name: 'Aerodynamic Wing Kit',
          quantity: 1,
          unitPrice: 385000,
          supplier: 'Porsche Centre SA',
        },
      ],
      totalAmount: 385000,
      priority: 'standard',
      vehicle: {
        make: 'Porsche',
        model: '911 GT3 RS',
        year: 2024,
      },
      shipping: {
        method: 'Ground Premium',
        courier: 'RAM Hand-to-Hand',
        address: 'Bryanston, Johannesburg',
      },
      documents: [
        { type: 'Invoice', url: '/documents/inv-003.pdf' },
        { type: 'Installation Guide', url: '/documents/install-003.pdf' },
      ],
    },
  ];

  const getStatusSteps = (status: string) => {
    const steps = ['Pending', 'Processing', 'Shipped', 'Delivered'];
    const currentStep = steps.indexOf(status.charAt(0).toUpperCase() + status.slice(1));
    return { steps, currentStep };
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'delivered':
        return 'success';
      case 'shipped':
        return 'info';
      case 'processing':
        return 'warning';
      case 'cancelled':
        return 'error';
      default:
        return 'default';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent':
        return 'error';
      case 'express':
        return 'warning';
      case 'standard':
        return 'info';
      default:
        return 'default';
    }
  };

  const formatCurrency = (amount: number) => {
    return `R ${amount.toLocaleString()}`;
  };

  const formatDate = (date: Date) => {
    return date.toLocaleDateString('en-ZA', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  const handleViewOrder = (order: Order) => {
    setSelectedOrder(order);
    setOpenDialog(true);
  };

  const handleNewOrderSubmit = () => {
    // Here you would typically make an API call to create the order
    // For now, we'll just close the dialog
    setOpenNewOrderDialog(false);
    // Reset form
    setNewOrder({
      partNumber: '',
      partName: '',
      quantity: 1,
      supplier: '',
      vehicle: {
        make: '',
        model: '',
        year: new Date().getFullYear(),
      },
      priority: 'standard',
    });
  };

  const handlePriorityChange = (event: SelectChangeEvent) => {
    setNewOrder({
      ...newOrder,
      priority: event.target.value,
    });
  };

  const handleFilterClick = () => {
    setOpenFilterDialog(true);
  };

  const handleFilterApply = () => {
    // Here you would typically filter the orders based on the selected filters
    setOpenFilterDialog(false);
  };

  const handleExportClick = () => {
    // Create CSV content
    const headers = ['Order ID', 'Date', 'Status', 'Priority', 'Vehicle', 'Supplier', 'Amount'];
    const csvContent = [
      headers.join(','),
      ...orders.map(order => [
        order.id,
        formatDate(order.orderDate),
        order.status,
        order.priority,
        `${order.vehicle.make} ${order.vehicle.model} ${order.vehicle.year}`,
        order.supplier,
        order.totalAmount
      ].join(','))
    ].join('\n');

    // Create and trigger download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `orders_export_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  return (
    <DashboardLayout>
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>
          Order Tracking
        </Typography>

        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography variant="h6" color="text.secondary">
                  Active Orders
                </Typography>
                <Typography variant="h4">
                  {orders.filter(order => order.status !== 'delivered' && order.status !== 'cancelled').length}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  In progress
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography variant="h6" color="text.secondary">
                  Total Value
                </Typography>
                <Typography variant="h4">
                  {formatCurrency(orders.reduce((sum, order) => sum + order.totalAmount, 0))}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  All orders
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography variant="h6" color="text.secondary">
                  Urgent Orders
                </Typography>
                <Typography variant="h4">
                  {orders.filter(order => order.priority === 'urgent').length}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  High priority
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography variant="h6" color="text.secondary">
                  Average Delivery
                </Typography>
                <Typography variant="h4">
                  4.5d
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Processing time
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Paper sx={{ p: 2, mb: 3 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                placeholder="Search orders..."
                InputProps={{
                  startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                }}
              />
            </Grid>
            <Grid item xs={12} md={8}>
              <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                <Button
                  variant="outlined"
                  startIcon={<FilterIcon />}
                  onClick={handleFilterClick}
                >
                  Filter
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<DownloadIcon />}
                  onClick={handleExportClick}
                >
                  Export
                </Button>
                <Button
                  variant="contained"
                  startIcon={<AssignmentIcon />}
                  onClick={() => setOpenNewOrderDialog(true)}
                >
                  New Order
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Paper>

        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Order Details</TableCell>
                <TableCell>Vehicle</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Shipping</TableCell>
                <TableCell>Amount</TableCell>
                <TableCell>Documents</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {orders.map((order) => (
                <TableRow key={order.id}>
                  <TableCell>
                    <Typography variant="subtitle2">{order.id}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {formatDate(order.orderDate)}
                    </Typography>
                    <Box sx={{ mt: 1 }}>
                      <Chip
                        label={order.priority}
                        size="small"
                        color={getPriorityColor(order.priority)}
                      />
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {order.vehicle.make} {order.vehicle.model}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {order.vehicle.year}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={order.status.charAt(0).toUpperCase() + order.status.slice(1)}
                      color={getStatusColor(order.status)}
                      size="small"
                    />
                    <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                      {order.trackingNumber}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {order.shipping.courier}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Expected: {formatDate(order.expectedDelivery)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight="bold">
                      {formatCurrency(order.totalAmount)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={1}>
                      {order.documents.map((doc, index) => (
                        <IconButton
                          key={index}
                          size="small"
                          color="primary"
                          onClick={() => window.open(doc.url, '_blank')}
                        >
                          <DescriptionIcon />
                        </IconButton>
                      ))}
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <IconButton
                      size="small"
                      onClick={() => handleViewOrder(order)}
                    >
                      <TimelineIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <Dialog
          open={openDialog}
          onClose={() => setOpenDialog(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            Order Details
          </DialogTitle>
          <DialogContent>
            {selectedOrder && (
              <Grid container spacing={3} sx={{ mt: 1 }}>
                <Grid item xs={12}>
                  <Stepper activeStep={getStatusSteps(selectedOrder.status).currentStep}>
                    {getStatusSteps(selectedOrder.status).steps.map((label) => (
                      <Step key={label}>
                        <StepLabel>{label}</StepLabel>
                      </Step>
                    ))}
                  </Stepper>
                </Grid>
                <Grid item xs={12}>
                  <Divider />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" gutterBottom>
                    Order Information
                  </Typography>
                  <Typography variant="body2">
                    Order ID: {selectedOrder.id}
                  </Typography>
                  <Typography variant="body2">
                    Date: {formatDate(selectedOrder.orderDate)}
                  </Typography>
                  <Typography variant="body2">
                    Tracking: {selectedOrder.trackingNumber}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" gutterBottom>
                    Vehicle Details
                  </Typography>
                  <Typography variant="body2">
                    {selectedOrder.vehicle.make} {selectedOrder.vehicle.model}
                  </Typography>
                  <Typography variant="body2">
                    Year: {selectedOrder.vehicle.year}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" gutterBottom>
                    Items
                  </Typography>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Part Number</TableCell>
                          <TableCell>Description</TableCell>
                          <TableCell align="right">Quantity</TableCell>
                          <TableCell align="right">Unit Price</TableCell>
                          <TableCell align="right">Total</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {selectedOrder.items.map((item) => (
                          <TableRow key={item.partNumber}>
                            <TableCell>{item.partNumber}</TableCell>
                            <TableCell>{item.name}</TableCell>
                            <TableCell align="right">{item.quantity}</TableCell>
                            <TableCell align="right">{formatCurrency(item.unitPrice)}</TableCell>
                            <TableCell align="right">{formatCurrency(item.quantity * item.unitPrice)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" gutterBottom>
                    Shipping Details
                  </Typography>
                  <Typography variant="body2">
                    Method: {selectedOrder.shipping.method}
                  </Typography>
                  <Typography variant="body2">
                    Courier: {selectedOrder.shipping.courier}
                  </Typography>
                  <Typography variant="body2">
                    Address: {selectedOrder.shipping.address}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" gutterBottom>
                    Documents
                  </Typography>
                  <Stack direction="row" spacing={1}>
                    {selectedOrder.documents.map((doc, index) => (
                      <Button
                        key={index}
                        variant="outlined"
                        size="small"
                        startIcon={<DescriptionIcon />}
                        onClick={() => window.open(doc.url, '_blank')}
                      >
                        {doc.type}
                      </Button>
                    ))}
                  </Stack>
                </Grid>
              </Grid>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenDialog(false)}>Close</Button>
          </DialogActions>
        </Dialog>

        {/* New Order Dialog */}
        <Dialog
          open={openNewOrderDialog}
          onClose={() => setOpenNewOrderDialog(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>Create New Order</DialogTitle>
          <DialogContent>
            <Grid container spacing={3} sx={{ mt: 1 }}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Part Number"
                  value={newOrder.partNumber}
                  onChange={(e) => setNewOrder({ ...newOrder, partNumber: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Part Name"
                  value={newOrder.partName}
                  onChange={(e) => setNewOrder({ ...newOrder, partName: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Quantity"
                  type="number"
                  value={newOrder.quantity}
                  onChange={(e) => setNewOrder({ ...newOrder, quantity: parseInt(e.target.value) || 1 })}
                  InputProps={{ inputProps: { min: 1 } }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Priority</InputLabel>
                  <Select
                    value={newOrder.priority}
                    label="Priority"
                    onChange={handlePriorityChange}
                  >
                    <MenuItem value="standard">Standard</MenuItem>
                    <MenuItem value="express">Express</MenuItem>
                    <MenuItem value="urgent">Urgent</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Supplier"
                  value={newOrder.supplier}
                  onChange={(e) => setNewOrder({ ...newOrder, supplier: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Vehicle Make"
                  value={newOrder.vehicle.make}
                  onChange={(e) => setNewOrder({
                    ...newOrder,
                    vehicle: { ...newOrder.vehicle, make: e.target.value }
                  })}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Vehicle Model"
                  value={newOrder.vehicle.model}
                  onChange={(e) => setNewOrder({
                    ...newOrder,
                    vehicle: { ...newOrder.vehicle, model: e.target.value }
                  })}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Vehicle Year"
                  type="number"
                  value={newOrder.vehicle.year}
                  onChange={(e) => setNewOrder({
                    ...newOrder,
                    vehicle: { ...newOrder.vehicle, year: parseInt(e.target.value) || new Date().getFullYear() }
                  })}
                  InputProps={{ inputProps: { min: 1900, max: new Date().getFullYear() + 1 } }}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenNewOrderDialog(false)}>Cancel</Button>
            <Button
              variant="contained"
              onClick={handleNewOrderSubmit}
              disabled={!newOrder.partNumber || !newOrder.partName || !newOrder.supplier}
            >
              Create Order
            </Button>
          </DialogActions>
        </Dialog>

        {/* Filter Dialog */}
        <Dialog
          open={openFilterDialog}
          onClose={() => setOpenFilterDialog(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Filter Orders</DialogTitle>
          <DialogContent>
            <Grid container spacing={3} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <FormControl component="fieldset">
                  <Typography variant="subtitle2" gutterBottom>
                    Status
                  </Typography>
                  <FormGroup>
                    {['pending', 'processing', 'shipped', 'delivered', 'cancelled'].map((status) => (
                      <FormControlLabel
                        key={status}
                        control={
                          <Checkbox
                            checked={filters.status.includes(status)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setFilters({
                                  ...filters,
                                  status: [...filters.status, status],
                                });
                              } else {
                                setFilters({
                                  ...filters,
                                  status: filters.status.filter((s) => s !== status),
                                });
                              }
                            }}
                          />
                        }
                        label={status.charAt(0).toUpperCase() + status.slice(1)}
                      />
                    ))}
                  </FormGroup>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <FormControl component="fieldset">
                  <Typography variant="subtitle2" gutterBottom>
                    Priority
                  </Typography>
                  <FormGroup>
                    {['standard', 'express', 'urgent'].map((priority) => (
                      <FormControlLabel
                        key={priority}
                        control={
                          <Checkbox
                            checked={filters.priority.includes(priority)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setFilters({
                                  ...filters,
                                  priority: [...filters.priority, priority],
                                });
                              } else {
                                setFilters({
                                  ...filters,
                                  priority: filters.priority.filter((p) => p !== priority),
                                });
                              }
                            }}
                          />
                        }
                        label={priority.charAt(0).toUpperCase() + priority.slice(1)}
                      />
                    ))}
                  </FormGroup>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Start Date"
                  type="date"
                  value={filters.dateRange.start}
                  onChange={(e) =>
                    setFilters({
                      ...filters,
                      dateRange: { ...filters.dateRange, start: e.target.value },
                    })
                  }
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="End Date"
                  type="date"
                  value={filters.dateRange.end}
                  onChange={(e) =>
                    setFilters({
                      ...filters,
                      dateRange: { ...filters.dateRange, end: e.target.value },
                    })
                  }
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Supplier"
                  value={filters.supplier}
                  onChange={(e) =>
                    setFilters({
                      ...filters,
                      supplier: e.target.value,
                    })
                  }
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenFilterDialog(false)}>Cancel</Button>
            <Button variant="contained" onClick={handleFilterApply}>
              Apply Filters
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </DashboardLayout>
  );
}; 